import type { Routemize_DTOs_UserSmallDTO } from '../../api/models/Routemize_DTOs_UserSmallDTO';

export interface TeamAvailabilityEligibility {
  /** All form members to request availability for: territory matches first, then the rest, in form order. */
  userIds: string[];
  /** Members not assigned to the customer's zip territory; empty when not in territories mode / no zip match. */
  outsideTerritoryUserIds: Set<string>;
}

/**
 * Resolves which booking-form members get an exact-time availability card (86bcwvyqm).
 * Every form member is included. When the customer's zip matches a territory, its members are
 * listed first and the rest are flagged as outside the territory (still bookable as an override).
 */
export function resolveTeamAvailabilityEligibility(
  formUsers: Routemize_DTOs_UserSmallDTO[],
  territoryMatchingUsers: Routemize_DTOs_UserSmallDTO[] | null | undefined,
): TeamAvailabilityEligibility {
  const formIds: string[] = [];
  const seen = new Set<string>();
  for (const user of formUsers) {
    if (!user.id || seen.has(user.id)) continue;
    seen.add(user.id);
    formIds.push(user.id);
  }

  const matchIds = new Set(
    (territoryMatchingUsers ?? [])
      .map((u) => u.id)
      .filter((id): id is string => !!id && seen.has(id)),
  );

  if (matchIds.size === 0) {
    return { userIds: formIds, outsideTerritoryUserIds: new Set<string>() };
  }

  const matches = formIds.filter((id) => matchIds.has(id));
  const outside = formIds.filter((id) => !matchIds.has(id));
  return { userIds: [...matches, ...outside], outsideTerritoryUserIds: new Set(outside) };
}
